import PropTypes from "prop-types";
import { useTranslations } from "next-intl";
import Tag from "../products/Tag";
import { formatPrice } from "@/utils/misc";
import { getBulkSaleInfo } from "@/utils/bulkDiscounts";

/**
 * A reusable component for displaying sale information in the cart
 * Handles both regular sales and bulk sales
 *
 * @param {Object} props
 * @param {Object} props.item - The cart item
 * @param {string} props.secondaryColor - Secondary color for styling
 * @param {string} props.secondaryTextColor - Secondary text color for styling
 * @param {string} props.className - Additional CSS classes
 */
const SaleInfo = ({
  item,
  secondaryColor,
  secondaryTextColor,
  className = "",
}) => {
  const t = useTranslations("Cart");

  const colors = {
    secondaryColor,
    secondaryTextColor,
  };

  // Check for regular sale
  const hasRegularSale = item.sale_price && item.regular_price &&
    parseFloat(item.regular_price) > parseFloat(item.sale_price);

  const regularSavings = hasRegularSale ?
    (parseFloat(item.regular_price) - parseFloat(item.sale_price)) * item.quantity : 0;

  const formattedRegularSavings = formatPrice(regularSavings);

  // Check for bulk sale
  const saleInfo = getBulkSaleInfo(item, t, colors);

  // If there's no sale at all, return null
  if (!hasRegularSale && !saleInfo) return null;

  // If there's only a regular sale (no bulk sale)
  if (hasRegularSale && !saleInfo) {
    return (
      <div className={`w-full text-xs mt-1 ${className}`}>
        <div className="flex">
          <Tag bgColor={secondaryColor} textColor="#FFFFFF">
            {t("sale")}
          </Tag>
          <div
            className="h-[30px] flex items-center px-3 ms-1 w-full bg-secondary-light-studio"
          >
            <span>{t('you_saved')} {formattedRegularSavings}</span>
          </div>
        </div>
      </div>
    );
  }

  // If there's a bulk sale (with or without regular sale)
  const {
    hasCompleteSets,
    hasPartialNextSet,
    remainingToQualify,
    formattedCurrentSavings,
    formattedPotentialSavings,
    tagText,
    tagBgColor,
    tagTextColor,
  } = saleInfo;

  return (
    <div className={`w-full text-xs mt-1 ${className}`}>
      <div className="flex">
        <Tag bgColor={tagBgColor} textColor={tagTextColor}>
          {tagText}
        </Tag>
        <div
          className="h-[30px] flex items-center px-3 ms-1 w-full bg-secondary-light-studio"
        >
          {hasCompleteSets && (
            <span>{t('you_saved')} {formattedCurrentSavings}</span>
          )}

          {hasCompleteSets && hasPartialNextSet && (
            <span>, </span>
          )}

          {hasPartialNextSet && (
            <span>{t('add_x_more_to_save', { x: remainingToQualify, y: formattedPotentialSavings })}</span>
          )}

          {!hasCompleteSets && !hasPartialNextSet && (
            <span>{t('add_x_more_to_save', { x: remainingToQualify, y: formattedPotentialSavings })}</span>
          )}
        </div>
      </div>
    </div>
  );
};

SaleInfo.propTypes = {
  item: PropTypes.object.isRequired,
  secondaryColor: PropTypes.string.isRequired,
  secondaryTextColor: PropTypes.string.isRequired,
  className: PropTypes.string,
};

export default SaleInfo;